import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faStar } from "@fortawesome/pro-solid-svg-icons/faStar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import notification from "antd/es/notification";
import { useIntl } from "react-intl";

import { MapFilterEntityType } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { MapSavedFilter } from "@mapmycustomers/shared/types/viewModel/SavedFilter";

import ButtonLink from "@app/component/ButtonLink";
import RoundButton from "@app/component/RoundButton";
import useMapView from "@app/component/view/utils/useMapView";
import { selectSavedFilter } from "@app/store/savedFilters/actions";
import useClearMapFilters from "@app/util/hook/useClearMapFilters";
import { MAP_ENTITY_TYPES } from "@app/util/map/consts";
import { getEntityTypeDisplayName } from "@app/util/ui";

import styles from "./SavedFiltersButton.module.scss";
import useStatus from "./utils/useStatus";
interface Props {
  implicitFilterFields?: IField[];
  savedFilter: MapSavedFilter;
  selectSavedFilter: typeof selectSavedFilter;
}

const SavedFilterButton: React.FC<Props> = ({
  implicitFilterFields,
  savedFilter,
  selectSavedFilter,
}) => {
  const {
    updateViewState,
    viewState: { filter },
  } = useMapView();

  const resetAllFilters = useClearMapFilters(implicitFilterFields);

  const intl = useIntl();
  const enabled = useStatus(savedFilter.filters, filter, implicitFilterFields, true);
  const { updateViewStateAndFetch } = useMapView();

  const handleShowAnyway = useCallback(() => {
    updateViewStateAndFetch({ request: { visibleEntities: MAP_ENTITY_TYPES } });
  }, [updateViewStateAndFetch]);

  const handleClick = useCallback(() => {
    updateViewState({ selectedSavedFilterId: enabled ? undefined : savedFilter.id });
    selectSavedFilter({
      entityType: savedFilter.type,
      savedFilter: enabled ? undefined : savedFilter,
    });
    if (enabled) {
      resetAllFilters();
    } else {
      const appliedFilterEntityTypes = Object.keys(savedFilter.filters) as MapFilterEntityType[];
      const hiddenEntities = appliedFilterEntityTypes.every((entity) => entity === "universal")
        ? []
        : MAP_ENTITY_TYPES.filter((entity) => !appliedFilterEntityTypes.includes(entity));
      if (hiddenEntities.length > 0) {
        notification.info({
          description: (
            <>
              <span className={styles.notificationDescription}>
                {intl.formatMessage({
                  id: "map.saveFilter.hiddenEntities.notification.description",
                  defaultMessage: "The applied filter is not relevant to these entities.",
                  description:
                    "Notification for the user applies a saved quick filter for one of the entities, others are hidden then",
                })}
              </span>
              <ButtonLink onClick={handleShowAnyway}>
                {intl.formatMessage({
                  id: "map.filters.hiddenEntities.notification.showAnyway",
                  defaultMessage: "Show Anyway",
                  description: " shows other entities",
                })}
              </ButtonLink>
            </>
          ),
          message: intl.formatMessage(
            {
              id: "map.saveFilter.hiddenEntities.notification",
              defaultMessage: "{entityList} Hidden",
              description:
                "Notification show when user apply saved quick filter for one of entities, other are hidden then",
            },
            {
              entityList: intl.formatList(
                hiddenEntities.map((entityType) =>
                  getEntityTypeDisplayName(intl, entityType, { lowercase: false, plural: true })
                ),
                { type: "conjunction" }
              ),
            }
          ),
        });
      }
    }
  }, [
    enabled,
    handleShowAnyway,
    intl,
    resetAllFilters,
    savedFilter,
    selectSavedFilter,
    updateViewState,
  ]);

  return (
    <RoundButton
      active={enabled}
      leftIcon={<FontAwesomeIcon className={styles.favoriteIcon} icon={faStar} size="1x" />}
      onClick={handleClick}
    >
      {savedFilter.name}
    </RoundButton>
  );
};

const mapDispatchToProps = {
  selectSavedFilter,
};

export default connect(null, mapDispatchToProps)(SavedFilterButton);
